import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import * as Mui from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import {
  designColors,
  designRadii,
  designShell,
  APP_BAR_OFFSET,
  PANEL_DRAWER_WIDTH,
  panelDrawerSx,
} from "services/constants/designTokens";
import {
  useExpandedContentLeft,
  GlobalBreadcrumb,
} from "components/DesignSystem/sharedUiPatterns";
import { useExpandablePanel } from "services/hooks/useExpandablePanel";
import { OpenFullViewIcon } from "components/DesignSystem/OpenFullViewIcon";
import { tooltipStyleProps } from "services/constants/Styles";

// One shared side-panel shell for every "slide-in detail / form" drawer in the
// app. It owns the three things that used to be hand-rolled (and drifted) per
// screen:
//   1. collapsed width           -> PANEL_DRAWER_WIDTH (designTokens)
//   2. open behaviour            -> right-anchored MUI Drawer
//   3. expand / collapse         -> full-page portal swap (APP_BAR_OFFSET +
//                                   useExpandedContentLeft, same as the
//                                   Administration CompanyDetailPanel)
// Change any of those here and every consumer updates at once — which is the
// whole point of routing MyZone (and Administration) panels through this
// instead of each one wrapping its own <Drawer sx={drawerStyle}>.
//
// Header/body/footer are slots so each screen keeps its own content while the
// chrome (title row + expand + close, scroll region, sticky footer) stays
// identical everywhere.
// Header icon button for every side panel, in both modes (2026-10-01, user
// request): the expanded full view used to swap these for "Collapse" /
// "Close" / "Edit" text buttons. Same soft icon either way; the full view
// just uses a slightly bigger button and icon. Screens adding their own
// header actions (Edit, More actions) should use this too, passing the
// panel's expanded flag.
export const PanelHeaderIconButton = ({
  title,
  expanded = false,
  children,
  sx,
  ...buttonProps
}) => (
  <Mui.Tooltip arrow slotProps={tooltipStyleProps} title={title || ""}>
    {/* span keeps the tooltip working when the button is disabled */}
    <span style={{ display: "inline-flex" }}>
      <Mui.IconButton
        aria-label={buttonProps["aria-label"] ?? title}
        {...buttonProps}
        sx={{
          ...designShell.panelSoftIconButton,
          color: designColors.slate500,
          width: expanded ? 40 : 32,
          height: expanded ? 40 : 32,
          "& .MuiSvgIcon-root": { fontSize: expanded ? 24 : 20 },
          // Full view: a visible fill + border, so the icons read as buttons
          // like the old text buttons did (the soft #F8FAFC fill vanishes on
          // the white header). The side panel keeps the soft look.
          ...(expanded
            ? {
                backgroundColor: designColors.slate100,
                border: `1px solid ${designColors.slate200}`,
                color: designColors.slate600,
                "&:hover": { backgroundColor: designColors.slate200 },
              }
            : {}),
          ...sx,
        }}
      >
        {children}
      </Mui.IconButton>
    </span>
  </Mui.Tooltip>
);

export const GlobalSidePanel = ({
  open,
  onClose,
  // Optional controlled expand state. When omitted the panel manages its own
  // expand/collapse internally (reset to collapsed whenever it closes), so a
  // consumer only has to pass `open` + `onClose` to get the full behaviour.
  expanded: expandedProp,
  onToggleExpand: onToggleExpandProp,
  width = PANEL_DRAWER_WIDTH,
  title,
  subtitle,
  // Fully custom header left-block; overrides title/subtitle when provided.
  header,
  // Extra controls (e.g. an Edit button) rendered just left of expand/close.
  headerActions,
  // Extra controls (e.g. a ⋮ menu) rendered after the close control.
  trailingActions,
  // Optional breadcrumb trail shown above the header while expanded.
  breadcrumb,
  footer,
  bodySx = {},
  headerSx = {},
  hideClose = false,
  hideExpand = false,
  children,
  // Escape hatch for a consumer that needs a wider collapsed drawer for a
  // specific screen (kept opt-in so the default stays the shared token).
  ModalProps,
  PaperProps,
  // Opt-in stacking for the expanded overlay, for a full view opened on top
  // of another (drawer) panel.
  expandedZIndex = 10,
}) => {
  const internal = useExpandablePanel(open);
  const expanded = expandedProp ?? internal.expanded;
  const onToggleExpand = onToggleExpandProp ?? internal.toggleExpanded;
  const contentLeft = useExpandedContentLeft(expanded);

  // Lock the page scroll while the full-page (expanded) overlay is up so the
  // body behind it can't scroll under the panel — mirrors CompanyDetailPanel.
  useEffect(() => {
    if (!expanded) return undefined;
    if (typeof document === "undefined") return undefined;
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, [expanded]);

  const headerLeft = header ?? (
    <Mui.Box sx={{ minWidth: 0, flex: 1 }}>
      {title ? (
        <Mui.Typography
          sx={{
            fontSize: 18,
            fontWeight: 700,
            color: designColors.slate800,
            lineHeight: 1.3,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {title}
        </Mui.Typography>
      ) : null}
      {subtitle ? (
        <Mui.Typography
          sx={{ mt: 0.25, fontSize: 13, color: designColors.slate600 }}
        >
          {subtitle}
        </Mui.Typography>
      ) : null}
    </Mui.Box>
  );

  const controls = (
    <Mui.Stack direction="row" spacing={1} alignItems="center">
      {headerActions}
      {hideExpand ? null : (
        <PanelHeaderIconButton
          expanded={expanded}
          title={expanded ? "Collapse" : "Open full view"}
          onClick={onToggleExpand}
        >
          {expanded ? <CloseFullscreenIcon /> : <OpenFullViewIcon />}
        </PanelHeaderIconButton>
      )}
      {hideClose ? null : (
        <PanelHeaderIconButton expanded={expanded} title="Close" onClick={onClose}>
          <CloseIcon />
        </PanelHeaderIconButton>
      )}
      {trailingActions}
    </Mui.Stack>
  );

  const panelContent = (
    <Mui.Box
      sx={{
        width: expanded ? "100%" : width,
        maxWidth: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        boxSizing: "border-box",
        backgroundColor: designColors.white,
        borderRadius: expanded ? designRadii.card : 0,
      }}
      role="presentation"
    >
      <Mui.Box
        sx={{
          px: { xs: 2, sm: 3 },
          pt: expanded ? 2.5 : 3,
          pb: 2,
          borderBottom: `1px solid ${designColors.slate200}`,
          ...headerSx,
        }}
      >
        {expanded && breadcrumb ? <GlobalBreadcrumb trail={breadcrumb} /> : null}
        <Mui.Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 1.5, sm: 2 }}
          alignItems="flex-start"
          justifyContent="space-between"
        >
          {headerLeft}
          {controls}
        </Mui.Stack>
      </Mui.Box>

      <Mui.Box
        sx={{ flex: 1, minHeight: 0, overflowY: "auto", ...bodySx }}
      >
        {children}
      </Mui.Box>

      {footer ? (
        <Mui.Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 1.25,
            borderTop: `1px solid ${designColors.slate200}`,
            backgroundColor: designColors.white,
          }}
        >
          {footer}
        </Mui.Box>
      ) : null}
    </Mui.Box>
  );

  if (!open && !expanded) return null;

  if (expanded) {
    return createPortal(
      <Mui.Box
        sx={{
          position: "fixed",
          top: APP_BAR_OFFSET,
          left: `${contentLeft}px`,
          right: 0,
          bottom: 0,
          zIndex: expandedZIndex,
          overflow: "hidden",
          backgroundColor: designColors.pageBg,
          p: 2,
          boxSizing: "border-box",
        }}
      >
        <Mui.Box sx={{ height: "100%", minHeight: "100%", width: "100%" }}>
          {panelContent}
        </Mui.Box>
      </Mui.Box>,
      document.body,
    );
  }

  return (
    <Mui.Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={panelDrawerSx(width)}
      ModalProps={ModalProps}
      PaperProps={{
        ...PaperProps,
        sx: {
          backgroundColor: designColors.white,
          width: { xs: "100vw", sm: width },
          maxWidth: { xs: "100vw", sm: "92vw" },
          borderTopLeftRadius: { xs: 0, sm: 8 },
          borderBottomLeftRadius: { xs: 0, sm: 8 },
          overflow: "hidden",
          ...PaperProps?.sx,
        },
      }}
    >
      {panelContent}
    </Mui.Drawer>
  );
};

export default GlobalSidePanel;
